// =============================================
// useAuditStatus — read-only sygnały "co już zrobione" per mapa.
// 4 buttony w AuditPanel: Pobierz PAA / Przelicz audyt / Pełny audyt LLM / Napraw mapę.
// Empirycznie zweryfikowane sygnały DB na mapie Whiteever (2026-06-09).
// =============================================
//
// PAA: per-mapa heurystyka — ile nodów ma cached PAA w dataforseo_cache
//   (query = lower(unaccent(canonical_question bez ?)))
// Audyt 2 (deterministic): audit_score != null + audit_report.meta.llm_used = false
// Audyt 3 (LLM): audit_report.meta.llm_used = true + llm_cost_usd > 0
//   UWAGA: każdy kolejny audyt 2 lub Napraw nadpisuje audit_report → llm_used wraca na false.
//   Stan "kiedyś był LLM" nie da się odróżnić po nadpisaniu — pokazujemy stan z OBECNEGO audit_report.
// Napraw: last_repaired_at != null + repair_status

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface AuditActionStatus {
  /** ISO timestamp ostatniej akcji, null jeśli nigdy */
  lastAt: string | null;
  /** Krótki detail do badge (np. "248/248", "57/100 warn", "+$1.43") */
  detail?: string;
  /** Czy akcja jest "nadpisana" przez inny krok (głównie krok 3 LLM) */
  overwritten?: boolean;
}

export interface AuditStatusResult {
  paa: AuditActionStatus & { cached: number; total: number };
  deterministic: AuditActionStatus & { score: number | null };
  llm: AuditActionStatus & { score: number | null; cost: number };
  repair: AuditActionStatus & { status: string | null };
}

const EMPTY_RESULT: AuditStatusResult = {
  paa: { lastAt: null, cached: 0, total: 0 },
  deterministic: { lastAt: null, score: null },
  llm: { lastAt: null, score: null, cost: 0 },
  repair: { lastAt: null, status: null },
};

export function useAuditStatus(mapId: string | null | undefined) {
  return useQuery<AuditStatusResult>({
    queryKey: ["audit-status", mapId],
    queryFn: async () => {
      if (!mapId) return EMPTY_RESULT;

      // 1. Mapa: audit + repair fields
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: mapRow } = await (supabase as any)
        .from("thematic_maps")
        .select("audit_score, audited_at, audit_report, last_repaired_at, repair_status, repair_report")
        .eq("id", mapId)
        .maybeSingle();

      if (!mapRow) return EMPTY_RESULT;

      const meta = mapRow.audit_report?.meta || {};
      const llmUsed = meta.llm_used === true;
      const verdict = meta.overall_verdict || null;
      const llmCost = Number(meta.llm_cost_usd ?? 0);

      // 2. PAA: count nodów cached vs total. Query normalize: lower + unaccent + bez "?"
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: nodes } = await (supabase as any)
        .from("thematic_nodes")
        .select("canonical_question")
        .eq("map_id", mapId);

      const total = nodes?.length ?? 0;
      let cached = 0;
      let paaLastAt: string | null = null;

      if (total > 0) {
        // Build normalized queries (RPC unaccent niedostępne na client — robimy w JS).
        const normalized = (nodes as Array<{ canonical_question: string }>)
          .map((n) => normalizeQuery(n.canonical_question))
          .filter(Boolean);

        if (normalized.length > 0) {
          // Batch query: jedno IN dla wszystkich queries
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { data: hits } = await (supabase as any)
            .from("dataforseo_cache")
            .select("query, fetched_at, expires_at")
            .eq("query_type", "paa")
            .in("query", normalized);

          const now = Date.now();
          for (const h of (hits || []) as Array<{ query: string; fetched_at: string; expires_at: string }>) {
            // Liczy tylko świeże (expires_at > NOW)
            if (new Date(h.expires_at).getTime() > now) {
              cached++;
              if (!paaLastAt || h.fetched_at > paaLastAt) paaLastAt = h.fetched_at;
            }
          }
        }
      }

      // 3. Build status objects
      const repairDelta = mapRow.repair_report?.re_audit_delta || null;
      const dupesFixed = repairDelta?.duplicates_resolved ?? 0;
      const scoreBefore = repairDelta?.score_before ?? null;
      const scoreAfter = repairDelta?.score_after ?? null;

      return {
        paa: {
          lastAt: paaLastAt,
          cached,
          total,
          detail: total > 0 ? `${cached}/${total}` : undefined,
        },
        deterministic: {
          // Pokazujemy gdy audit istnieje i NIE jest LLM (wtedy LLM krok wygrywa display)
          // Ale nawet gdy llm_used=true, deterministic został LICZONY w tej samej sesji
          // — tylko sygnał "kiedy ostatnio deterministic SAM" gubi się przy LLM nadpisaniu.
          lastAt: mapRow.audited_at,
          score: mapRow.audit_score,
          detail:
            mapRow.audit_score != null
              ? `${mapRow.audit_score}/100${verdict ? " " + verdict : ""}`
              : undefined,
        },
        llm: {
          lastAt: llmUsed ? mapRow.audited_at : null,
          score: llmUsed ? mapRow.audit_score : null,
          cost: llmCost,
          // overwritten: audit_report istnieje (był jakiś audit) ale llm_used=false
          // i jest sygnał że LLM kiedyś był (cost > 0). W praktyce: gdy llm_used=false
          // i llm_cost = 0, traktujemy jako "nigdy nie odpalony" (false). Reliable
          // tylko CURRENT state — patrz komentarz na górze pliku.
          overwritten: false, // nie ma niezawodnego sygnału — patrz docstring
          detail:
            llmUsed && llmCost > 0
              ? `${mapRow.audit_score}/100 ${verdict} · $${llmCost.toFixed(2)}`
              : undefined,
        },
        repair: {
          lastAt: mapRow.last_repaired_at,
          status: mapRow.repair_status,
          detail:
            mapRow.last_repaired_at
              ? scoreBefore != null && scoreAfter != null
                ? `${scoreBefore}→${scoreAfter} · ${dupesFixed} dupes`
                : (mapRow.repair_status || "done")
              : undefined,
        },
      };
    },
    enabled: !!mapId,
    refetchInterval: 30_000, // odświeżaj co 30s żeby badge reflektował nowe akcje
    staleTime: 10_000,
  });
}

/**
 * Normalize canonical_question dla PAA cache lookup.
 * Empirycznie zweryfikowany format na DB Whiteever:
 *   "Od czego zacząć organizację ślubu i wesela?"
 *   → "od czego zaczac organizacje slubu i wesela"
 */
function normalizeQuery(raw: string | null | undefined): string {
  if (!raw) return "";
  return raw
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "") // remove combining diacritics
    .replace(/ł/g, "l")
    .replace(/Ł/g, "l")
    .replace(/[?!]/g, "")
    .trim();
}
